import { Link } from "react-router-dom";
import { Fragment } from "react";
import { motion } from "framer-motion";
import {
  ScanLine, Box, BrainCircuit, ClipboardList, TrendingUp, CheckCircle2,
  ArrowRight, Ruler, FileText, Droplets, Wrench, Layers,
  ShieldAlert, Eye, History, UserCheck,
  Building2, AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { INSIGHTS } from "@/data/insights";

const GOLD = "#C5A24A";
const CONTACT_EMAIL = "iainmunro@zentaraeng.com";
const CONTACT_HREF = `mailto:${CONTACT_EMAIL}?subject=Inspection%20enquiry`;
const SITE = "https://zentaraeng.com";

const STEPS = [
  { icon: Box, title: "CAPTURE", sub: "We 3D-scan your site" },
  { icon: BrainCircuit, title: "ANALYZE", sub: "AI first-cut detection, engineer-verified" },
  { icon: ClipboardList, title: "DOCUMENT", sub: "Engineer-grade reports" },
  { icon: TrendingUp, title: "TRACK", sub: "Change over time with confidence" },
];

const STRUCTURES = [
  { name: "Bridges", img: "https://images.pexels.com/photos/18849576/pexels-photo-18849576.jpeg?auto=compress&w=800&q=80" },
  { name: "Loading Docks", img: "https://images.unsplash.com/photo-1758789667762-56175fe4601c?w=800&q=80" },
  { name: "Façades", img: "https://images.unsplash.com/photo-1551038247-3d9af20df552?w=800&q=80" },
  { name: "Retaining Walls", img: "https://images.unsplash.com/photo-1785996757741-c46e909fd1c5?w=800&q=80" },
];

const FEATURES = [
  "AI-powered defect detection",
  "Precise measurements in 3D",
  "Engineer-grade reporting",
  "Track change. Reduce risk.",
  "Make data-driven decisions",
];

const SUMMARY = [
  { type: "Cracking", n: 12, w: 7, r: 5 },
  { type: "Spalling", n: 8, w: 4, r: 6 },
  { type: "Corrosion", n: 6, w: 3, r: 4 },
  { type: "Water Ingress", n: 5, w: 2, r: 3 },
];

const STATS = [
  { n: "100%", label: "surface coverage, every inspection" },
  { n: "2–3×", label: "faster capture than fixed-camera methods" },
  { n: "20–40 hrs", label: "saved per structure review" },
];

const BUILDING_CASES = [
  {
    slug: "elliot-lake", loc: "Algo Centre Mall — Elliot Lake, ON", date: "2012", toll: "2 killed · 20+ injured", tag: "Deficient inspection",
    source: { label: "Wikipedia", url: "https://en.wikipedia.org/wiki/Algo_Centre_Mall" },
    what: "A rooftop parking deck corroded through and collapsed into the mall below.",
    issue: "Years of water and salt penetration drove severe steel corrosion; an inspection weeks earlier declared the structure sound. PEO found the inspection deficient and urged more frequent review by qualified structural engineers where public safety requires it.",
    lesson: "Water ingress, chlorides and concrete deterioration in parkades need recurring structural review — not just visual patching.",
  },
  {
    slug: "deguire", loc: "135 Déguire Blvd — Montréal, QC", date: "2008", toll: "1 killed", tag: "Deferred maintenance",
    source: { label: "CBC News", url: "https://www.cbc.ca/news/canada/montreal/quebec-to-improve-building-inspections-1.928692" },
    what: "A large underground garage slab collapsed onto a vehicle.",
    issue: "Construction defects, reinforcement corrosion, concrete delamination, inadequate maintenance and deficient prior repairs. The incident drove far stricter Québec parking-garage inspection requirements.",
    lesson: "Aging slabs demand documented condition tracking and properly engineered repairs — not one-off patch jobs.",
  },
  {
    slug: "57-ann-street", loc: "57 Ann Street — New York City", date: "2023", toll: "1 killed", tag: "Unpermitted repair",
    source: { label: "Wikipedia", url: "https://en.wikipedia.org/wiki/2023_New_York_City_parking_garage_collapse" },
    what: "A multi-storey parking garage collapsed, killing one and injuring others.",
    issue: "NYC found the collapse stemmed from a repair project done without DOB approval or permits and without reporting deteriorating conditions; open violations existed for loose, defective and cracked concrete at the time.",
    lesson: "Known concrete distress plus unpermitted repair work is a major red flag — repairs need formal engineering review and controlled temporary works.",
  },
  {
    slug: "kingston", loc: "Underground parkade — Kingston, ON", date: "2010", toll: "No fatalities", tag: "Delayed action",
    source: { label: "CBC News", url: "https://www.cbc.ca/news/canada/ottawa/parkade-collapse-crushes-cars-in-kingston-1.943443" },
    what: "The upper level of a two-storey parkade collapsed onto the level below.",
    issue: "Severe deterioration from road salt, reinforcement corrosion, water infiltration and freeze-thaw; insufficient inspection and delayed maintenance let it progress unchecked.",
    lesson: "Salt-driven corrosion compounds fast — early, repeatable inspection is what catches it while repairs are still cheap.",
  },
  {
    slug: "rocky-mountain", loc: "Rocky Mountain Court — Calgary, AB", date: "2011", toll: "Collapse averted", tag: "Near-miss",
    source: { label: "CBC News", url: "https://www.cbc.ca/news/canada/calgary/rocky-mountain-court-parkade-failure-leads-to-landmark-court-decision-1.2822760" },
    what: "Rehabilitation work damaged the structural slab and created an unsafe condition; full collapse was narrowly avoided.",
    issue: "An Alberta court found the engineering work had undermined the parkade's structural stability, and the City prosecuted.",
    lesson: "Process failures cut both ways — repairs need the same rigour and documentation as inspections.",
  },
  {
    slug: "billingsley", loc: "1915 Billingsley Terrace — Bronx, NY", date: "2023", toll: "", tag: "Reactive enforcement",
    source: { label: "NYC Buildings", url: "https://www.nyc.gov/site/buildings/dob/pr-investigation-bx-building.page" },
    what: "A residential corner section collapsed.",
    issue: "NYC Council said it followed years of neglect and hundreds of violations, with 'glaring warning signs,' and criticised a largely reactive, complaint-based inspection approach.",
    lesson: "Complaint-based enforcement isn't enough for aging structures — high-risk buildings need proactive review and tracking of repeat defects.",
  },
  {
    slug: "champlain", loc: "Champlain Towers South — Surfside, FL", date: "2021", toll: "98 killed", tag: "Failure to act",
    source: { label: "NIST", url: "https://www.nist.gov/disaster-failure-studies/champlain-towers-south-collapse-investigation" },
    what: "A residential tower collapsed; the pool-deck / garage transfer-slab area failed first.",
    issue: "NIST's investigation has focused on the pool deck failing first and on longstanding deterioration and distress in the lower structural system.",
    lesson: "Parking / podium-slab deterioration under occupied buildings is a structural life-safety issue, not a cosmetic one.",
  },
];

const BRIDGE_CASES = [
  {
    slug: "fern-hollow", loc: "Fern Hollow Bridge — Pittsburgh, PA", date: "2022", toll: "10 injured", tag: "No follow-through",
    source: { label: "Wikipedia", url: "https://en.wikipedia.org/wiki/Fern_Hollow_Bridge" },
    what: "The bridge collapsed into Frick Park.",
    issue: "NTSB's probable cause was corrosion and section loss from the city's failure to act on repeated repair recommendations in inspection reports, plus poor-quality inspections and insufficient oversight.",
    lesson: "Inspections only work if owners close the loop and complete the repairs they recommend.",
  },
  {
    slug: "mianus-river", loc: "Mianus River Bridge — Greenwich, CT", date: "1983", toll: "3 killed", tag: "Fracture-critical",
    source: { label: "Wikipedia", url: "https://en.wikipedia.org/wiki/Mianus_River_Bridge" },
    what: "A suspended span of I-95 collapsed.",
    issue: "NTSB found the probable cause was corrosion-driven displacement due to deficiencies in the bridge's safety-inspection and maintenance program.",
    lesson: "Fracture-critical members and pin/hanger assemblies need targeted inspection methods — not generic walk-throughs.",
  },
  {
    slug: "hatchie-river", loc: "Hatchie River Bridge — Covington, TN", date: "1989", toll: "8 killed", tag: "Scour / substructure",
    source: { label: "NTSB report", url: "https://www.ntsb.gov/investigations/AccidentReports/Reports/HAR9001.pdf" },
    what: "Three bridge spans collapsed.",
    issue: "NTSB said Tennessee DOT failed to evaluate and correct migration of the main river channel, and flagged issues with inspection procedures, report review and maintenance guidelines.",
    lesson: "Structures over water need scour monitoring and substructure / underwater inspection — not just deck checks.",
  },
  {
    slug: "schoharie-creek", loc: "Schoharie Creek Bridge — NY", date: "1987", toll: "10 killed", tag: "Hidden foundations",
    source: { label: "Wikipedia", url: "https://en.wikipedia.org/wiki/Schoharie_Creek_Bridge_collapse" },
    what: "A New York Thruway bridge collapsed during flooding.",
    issue: "NTSB cites Schoharie Creek, with Hatchie, as a key case that led FHWA to require comprehensive underwater inspection.",
    lesson: "If a structure has hidden foundations or scour exposure, standard visual inspection is not enough.",
  },
  {
    slug: "silver-bridge", loc: "Silver Bridge — Point Pleasant, WV", date: "1967", toll: "46 killed", tag: "Systematic regime",
    source: { label: "Wikipedia", url: "https://en.wikipedia.org/wiki/Silver_Bridge" },
    what: "The bridge collapsed completely into the Ohio River.",
    issue: "NTSB highlights Silver Bridge as the case that led to the National Bridge Inspection Standards in 1970 — the danger of operating critical structures without a systematic inspection regime.",
    lesson: "Critical members need redundancy, traceable inspections and national standards for periodic review.",
  },
];

const THEMES = [
  "Water ingress + chlorides + corrosion",
  "Deferred repairs",
  "Open defects never escalated",
  "Visual inspections with no follow-through",
  "Poor record keeping",
  "Unpermitted or poorly planned repairs",
  "Hidden, hard-to-access elements left unassessed",
];

function Pin({ label, icon: Icon, className }) {
  return (
    <div className={`absolute z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full border-2 bg-[#09090B]/80 backdrop-blur-md ${className}`}
      style={{ borderColor: GOLD, color: GOLD, boxShadow: "0 0 15px rgba(212,168,75,0.3)" }}>
      <Icon className="w-3 h-3" />
      <span className="text-[10px] font-semibold tracking-wide whitespace-nowrap">{label}</span>
    </div>
  );
}

function ChangeChart() {
  // static demo multi-line chart
  const w = 300, h = 90, pad = 8;
  const total = [50, 48, 46, 44, 42, 40];
  const worse = [8, 10, 12, 14, 16, 18];
  const resolved = [2, 6, 10, 12, 14, 16];
  const max = 60;
  const pts = (arr) => arr.map((v, i) => `${pad + (i * (w - pad * 2)) / (arr.length - 1)},${h - pad - (v / max) * (h - pad * 2)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24" data-testid="change-chart">
      <polyline points={pts(total)} fill="none" stroke="#E5E7EB" strokeWidth="1.6" />
      <polyline points={pts(worse)} fill="none" stroke="#EA580C" strokeWidth="1.6" />
      <polyline points={pts(resolved)} fill="none" stroke={GOLD} strokeWidth="1.6" />
    </svg>
  );
}

function CaseCard({ c }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="border border-[#27272A] bg-[#18181B] rounded-xl p-6 flex flex-col" data-testid={`case-${c.slug}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-xs px-2 py-1 rounded" style={{ background: "rgba(197,162,74,0.15)", color: GOLD }}>{c.date}</span>
        {c.toll && <span className="text-[11px] uppercase tracking-wider text-[#71717A]">{c.toll}</span>}
      </div>
      <h3 className="font-display text-lg font-bold mt-3 flex items-start gap-2">
        <Building2 className="w-4 h-4 mt-1 shrink-0" style={{ color: GOLD }} /> {c.loc}
      </h3>
      <p className="text-sm text-[#D4D4D8] mt-2">{c.what}</p>
      <p className="text-sm text-[#A1A1AA] mt-3 leading-relaxed">
        <span className="label-caps block mb-1" style={{ color: GOLD }}>Inspection / maintenance issue</span>
        {c.issue}
      </p>
      <p className="text-sm text-white mt-3 leading-relaxed border-l-2 pl-3" style={{ borderColor: GOLD }}>
        <span className="label-caps block mb-1" style={{ color: GOLD }}>Lesson</span>
        {c.lesson}
      </p>
      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border" style={{ borderColor: "rgba(197,162,74,0.4)", color: GOLD }}>{c.tag}</span>
        {c.source && (
          <a href={c.source.url} target="_blank" rel="noreferrer" className="text-[11px] text-[#71717A] hover:text-[#C5A24A] underline underline-offset-2 shrink-0" data-testid={`case-source-${c.slug}`}>Source: {c.source.label}</a>
        )}
      </div>
    </motion.div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const appHref = user ? "/dashboard" : "/login";
  const appLabel = user ? "Open dashboard" : "Client login";

  return (
    <div className="min-h-screen bg-[#09090B] text-[#FAFAFA] grain">
      {/* Nav */}
      <header className="sticky top-0 z-40 h-16 border-b border-[#27272A] bg-[#09090B]/90 backdrop-blur flex items-center justify-between px-6 md:px-12">
        <div className="flex items-center gap-2">
          <img src="/zentara-logo.png" alt="Zentara Integrity" className="w-8 h-8 object-contain" />
          <span className="font-display font-bold text-lg">Zentara <span className="text-[#C5A24A]">Integrity</span></span>
        </div>
        <div className="flex items-center gap-4">
          <a href="#case-studies" className="hidden sm:inline text-sm text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-case-studies">Case studies</a>
          <Link to="/insights" className="hidden sm:inline text-sm text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-insights">Insights</Link>
          <a href="#structures" className="hidden sm:inline text-sm text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-structures">Structures</a>
          <Link to={appHref} className="text-sm text-[#A1A1AA] hover:text-white transition-colors" data-testid="landing-client-login">{appLabel}</Link>
          <a href={CONTACT_HREF} data-testid="landing-request-top">
            <Button className="gap-2 font-semibold text-[#09090B] hover:-translate-y-0.5 transition-transform" style={{ background: GOLD }}>
              Request an inspection <ArrowRight className="w-4 h-4" />
            </Button>
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1727238451791-af16e7f7999b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="Concrete parking structure" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090B] via-[#09090B]/85 to-[#09090B]/40" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="gold-rule pl-6">
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]">
                Engineer-Grade<br />Concrete Inspections
              </h1>
              <p className="mt-5 text-lg md:text-xl" style={{ color: GOLD }}>
                Fully managed by Zentara — we scan, inspect and report.
              </p>
            </div>
            <p className="mt-6 max-w-md text-[#A1A1AA] leading-relaxed">
              Our engineers 3D-scan your structure and use AI to make the first cut at every defect — then verify each one by hand, measure in real 3D space, and deliver a professional engineering report you can act on.
            </p>
            <div className="mt-8 flex items-center gap-3">
              <a href={CONTACT_HREF} data-testid="landing-cta-primary">
                <Button className="gap-2 h-11 px-6 font-semibold text-[#09090B] hover:-translate-y-0.5 transition-transform" style={{ background: GOLD }}>
                  Request an inspection <ArrowRight className="w-4 h-4" />
                </Button>
              </a>
              <a href="#structures">
                <Button variant="outline" className="h-11 px-6 border-[#3f3f46] bg-transparent hover:bg-[#18181B] hover:text-white" data-testid="landing-cta-secondary">
                  See structures
                </Button>
              </a>
            </div>
          </motion.div>

          {/* Floating defect card demo with pins */}
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.15 }}
            className="relative hidden lg:block h-[420px]">
            <Pin label="Water Ingress" icon={Droplets} className="top-2 right-16" />
            <Pin label="Corrosion" icon={Wrench} className="top-24 right-0" />
            <Pin label="Spalling" icon={Layers} className="bottom-6 right-24" />
            <div className="absolute left-0 top-8 w-72 bg-[#F8F9FA] text-[#09090B] rounded-lg shadow-2xl overflow-hidden border border-zinc-200"
              data-testid="demo-defect-card">
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <ScanLine className="w-4 h-4" style={{ color: "#A8863A" }} />
                  <span className="font-display font-bold">Crack detected</span>
                </div>
                <span className="inline-block mt-2 px-3 py-1 rounded text-[10px] font-bold uppercase tracking-widest"
                  style={{ background: "rgba(212,168,75,0.2)", color: "#A8863A" }}>Moderate</span>
                <div className="grid grid-cols-2 gap-y-1.5 text-sm border-t border-zinc-200 mt-3 pt-3">
                  <span className="text-zinc-500">Width</span><span className="text-right font-medium">0.38 mm</span>
                  <span className="text-zinc-500">Length</span><span className="text-right font-medium">1.42 m</span>
                  <span className="text-zinc-500">Orientation</span><span className="text-right font-medium">Vertical</span>
                  <span className="text-zinc-500">Location</span><span className="text-right font-medium">Grid B2 · Beam 14</span>
                </div>
              </div>
              <div className="relative h-32 bg-zinc-300">
                <img src="https://images.unsplash.com/photo-1551038247-3d9af20df552?w=600&q=70" alt="crack" className="w-full h-full object-cover" />
                <span className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/70 px-2 py-1 rounded text-xs font-mono" style={{ color: GOLD }}>
                  <Ruler className="w-3 h-3" /> 1.42 m
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why it matters */}
      <section className="border-y border-[#27272A] bg-[#0C0C0E]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20 grid lg:grid-cols-12 gap-10 items-start" data-testid="why-it-matters">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 mb-4">
              <ShieldAlert className="w-4 h-4" style={{ color: GOLD }} />
              <p className="label-caps" style={{ color: GOLD }}>Why it matters</p>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight leading-tight">
              The warning signs live where no one is looking.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[#A1A1AA] leading-relaxed">
            <p>From the outside a concrete structure looks simple. It is anything but. Post-tensioned slabs carry dynamic loads across long spans, drainage moves serious volumes of water, and construction joints open quiet pathways for moisture — all while the interior cycles through wet, freeze, thaw and chemical exposure, season after season.</p>
            <p>When investigators examined the 2021 collapse of <a href="https://www.boston.com/real-estate/national-news/2024/03/08/surfside-collapse-investigators-faulty-columns/" target="_blank" rel="noreferrer" className="text-white underline decoration-[#C5A24A]/50 underline-offset-4 hover:decoration-[#C5A24A] transition-colors" data-testid="surfside-link">Champlain Towers South</a> in Surfside, Florida — which killed 98 people — they traced the failure to the garage below, where columns had lost strength and reinforcement had badly corroded. The garage was where the warning signs lived. It was also where they went unseen.</p>
            <p className="text-white font-medium border-l-2 pl-4" style={{ borderColor: GOLD }}>Cracking, delamination, water ingress and corrosion are all manageable — if they are caught early. Left undetected, they compound.</p>
          </div>
        </div>
      </section>

      {/* Case studies / failures */}
      <section id="case-studies" className="border-b border-[#27272A] bg-[#0C0C0E]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-4 h-4" style={{ color: GOLD }} />
              <p className="label-caps" style={{ color: GOLD }}>Lessons written in concrete</p>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight leading-tight">
              These structures were inspected. The deterioration was still missed.
            </h2>
            <p className="mt-4 text-[#A1A1AA] leading-relaxed">
              None of these were simply "un-inspected" structures. In case after case the warning signs were documented, or there to be found — the failure lay in inadequate inspection, missed severity, deferred repairs, or no follow-through on known defects. Systematic visual records, 3D spatial documentation and condition tracking over time exist to close exactly that gap.
            </p>
          </div>

          {/* Developing incident — featured, current */}
          <Link
            to="/insights/empire-towers-glen-burnie"
            className="group mt-10 block rounded-2xl border overflow-hidden hover:border-[#FF9500]/60 transition-colors"
            style={{ borderColor: "rgba(255,149,0,0.35)", background: "rgba(255,149,0,0.04)" }}
            data-testid="featured-empire-towers"
          >
            <div className="grid md:grid-cols-[1.6fr_1fr]">
              <div className="p-7 md:p-8">
                <div className="flex items-center gap-2.5">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-70" style={{ background: "#FF9500" }} />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: "#FF9500" }} />
                  </span>
                  <span className="label-caps" style={{ color: "#FF9500" }}>Developing · Current case</span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-black tracking-tight mt-3 leading-tight">
                  Empire Towers, Glen Burnie — a parkade warning five years early
                </h3>
                <p className="text-sm md:text-[15px] text-[#A1A1AA] mt-3 leading-relaxed max-w-2xl">
                  On 9 July 2026 the underground garage beneath a 10-storey Maryland tower partially failed during unpermitted construction — sagging slabs, exposed reinforcement and compromised columns. A resident says she photographed cracking columns and asked for an inspection back in 2021.
                </p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: GOLD }}>
                  Read the breakdown <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
              <div className="border-t md:border-t-0 md:border-l border-[#27272A] p-7 md:p-8 grid grid-cols-2 gap-5 content-center bg-[#0A0A0C]">
                {[["150 ft", "collapse zone drawn"], ["~1,500", "shoring posts installed"], ["~100", "vehicles trapped"], ["5 yrs", "since the first warning"]].map(([n, l]) => (
                  <div key={l} data-testid="empire-stat">
                    <p className="font-display text-2xl md:text-3xl font-black" style={{ color: GOLD }}>{n}</p>
                    <p className="text-xs text-[#A1A1AA] mt-1 leading-snug">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </Link>

          <p className="label-caps mt-12 mb-5" style={{ color: GOLD }}>Buildings &amp; parkades</p>
          <div className="grid md:grid-cols-2 gap-5">
            {BUILDING_CASES.map((c) => <CaseCard key={c.slug} c={c} />)}
          </div>

          <p className="label-caps mt-12 mb-5" style={{ color: GOLD }}>Bridges &amp; major structures</p>
          <div className="grid md:grid-cols-2 gap-5">
            {BRIDGE_CASES.map((c) => <CaseCard key={c.slug} c={c} />)}
          </div>

          <div className="mt-14 border border-[#27272A] bg-[#18181B] rounded-xl p-7 md:p-8" data-testid="themes-panel">
            <h3 className="font-display text-xl md:text-2xl font-bold">The pattern repeats — and it's preventable</h3>
            <p className="text-[#A1A1AA] mt-2 text-sm max-w-3xl">Across decades and structure types, the same failure modes keep surfacing. Every one is something a disciplined, repeatable inspection record is built to catch early.</p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {THEMES.map((t, idx) => (
                <span key={t} className="text-xs px-3 py-1.5 rounded-full border text-[#D4D4D8]" style={{ borderColor: "rgba(197,162,74,0.35)" }} data-testid={`theme-${idx}`}>{t}</span>
              ))}
            </div>
          </div>

          <p className="mt-8 text-xs text-[#71717A] max-w-3xl leading-relaxed">
            A Government of Canada study of 300+ parking structures found road salt and water penetration driving reinforcing-steel corrosion to be the principal cause of deterioration. CMHC guidance recommends periodic — including annual visual — inspection, because early detection and repair costs a fraction of letting deterioration become extensive.
          </p>
        </div>
      </section>

      {/* Stats strip */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-14 grid sm:grid-cols-3 gap-8">
        {STATS.map((s, i) => (
          <motion.div key={s.n} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: i * 0.08 }} className="border-t-2 pt-5" style={{ borderColor: GOLD }} data-testid={`stat-${i}`}>
            <p className="font-display text-4xl md:text-5xl font-black" style={{ color: GOLD }}>{s.n}</p>
            <p className="mt-2 text-sm text-[#A1A1AA]">{s.label}</p>
          </motion.div>
        ))}
      </section>

      {/* Workflow steps */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {STEPS.map((s, i) => (
          <motion.div key={s.title} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: i * 0.06 }} className="border border-[#27272A] bg-[#18181B] rounded-xl p-6" data-testid={`step-${s.title.toLowerCase()}`}>
            <div className="w-10 h-10 rounded-lg border-2 flex items-center justify-center" style={{ borderColor: GOLD }}>
              <s.icon className="w-5 h-5" style={{ color: GOLD }} />
            </div>
            <p className="label-caps mt-4" style={{ color: GOLD }}>{s.title}</p>
            <p className="text-sm text-[#A1A1AA] mt-1">{s.sub}</p>
          </motion.div>
        ))}
      </section>

      {/* Consistency + living record */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-16 grid md:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="border border-[#27272A] bg-[#18181B] rounded-xl p-7" data-testid="value-consistency">
          <div className="w-10 h-10 rounded-lg border-2 flex items-center justify-center" style={{ borderColor: GOLD }}>
            <Eye className="w-5 h-5" style={{ color: GOLD }} />
          </div>
          <h3 className="font-display text-xl font-bold mt-4">Consistency, not fatigue</h3>
          <p className="text-[#A1A1AA] mt-2 leading-relaxed text-sm">A detection model doesn't tire on the third level. It applies the same criteria to every surface it sees — no soffit missed to poor lighting, no stain dismissed as minor. Every anomaly is flagged, logged and located in the 3D scan.</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08 }}
          className="border border-[#27272A] bg-[#18181B] rounded-xl p-7" data-testid="value-living-record">
          <div className="w-10 h-10 rounded-lg border-2 flex items-center justify-center" style={{ borderColor: GOLD }}>
            <History className="w-5 h-5" style={{ color: GOLD }} />
          </div>
          <h3 className="font-display text-xl font-bold mt-4">From one-off report to living asset record</h3>
          <p className="text-[#A1A1AA] mt-2 leading-relaxed text-sm">Each survey is compared directly against the last. A crack that was hairline last visit and is now measurably wider tells a very different story than one that has held steady — giving you a longitudinal view of condition, not just a snapshot.</p>
        </motion.div>
      </section>

      {/* Inspection report summary panel */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-16">
        <div className="bg-[#18181B] border border-[#27272A] rounded-xl p-6 md:p-8 grid md:grid-cols-2 gap-8" data-testid="summary-panel">
          <div>
            <p className="label-caps text-[#A1A1AA]">Inspection Report</p>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex items-center gap-3"><FileText className="w-4 h-4" style={{ color: GOLD }} /><div><p className="font-medium">May 12, 2026 · Inspection 1</p></div></div>
              <div className="flex items-center gap-3"><FileText className="w-4 h-4" style={{ color: GOLD }} /><div><p className="font-medium">Jun 10, 2026 · Inspection 2</p></div></div>
              <div className="flex items-center gap-3"><FileText className="w-4 h-4" style={{ color: GOLD }} /><div><p className="font-medium">PDF Export · Engineer-Ready</p></div></div>
            </div>
            <p className="label-caps text-[#A1A1AA] mt-8 mb-2">Change over time</p>
            <ChangeChart />
            <div className="flex items-center gap-4 mt-2 text-[11px] text-[#A1A1AA]">
              <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#E5E7EB]" /> Total</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#EA580C]" /> Worsening</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-0.5" style={{ background: GOLD }} /> Resolved</span>
            </div>
          </div>
          <div>
            <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr] gap-y-2 text-sm">
              <span className="label-caps text-[#A1A1AA]">Defect</span>
              <span className="label-caps text-[#A1A1AA] text-right">New</span>
              <span className="label-caps text-[#A1A1AA] text-right">Worse</span>
              <span className="label-caps text-[#A1A1AA] text-right">Resolved</span>
              {SUMMARY.map((row) => (
                <Fragment key={row.type}>
                  <span className="py-2 border-t border-[#27272A]">{row.type}</span>
                  <span className="py-2 border-t border-[#27272A] text-right font-mono">{row.n}</span>
                  <span className="py-2 border-t border-[#27272A] text-right font-mono" style={{ color: "#EA580C" }}>{row.w}</span>
                  <span className="py-2 border-t border-[#27272A] text-right font-mono" style={{ color: GOLD }}>{row.r}</span>
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Structures */}
      <section id="structures" className="max-w-6xl mx-auto px-6 md:px-12 pb-16">
        <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight">ONE WORKFLOW. MANY STRUCTURES.</h2>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {STRUCTURES.map((s, i) => (
            <motion.div key={s.name} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.05 }} className="group border border-[#27272A] rounded-xl overflow-hidden bg-[#18181B]" data-testid={`structure-${s.name.toLowerCase().replace(/\s|ç/g, "-")}`}>
              <div className="h-32 overflow-hidden">
                <img src={s.img} alt={s.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
              </div>
              <p className="px-4 py-3 font-display font-semibold" style={{ color: GOLD }}>{s.name}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* What AI doesn't replace */}
      <section className="border-y border-[#27272A] bg-[#0C0C0E]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20 grid lg:grid-cols-12 gap-10 items-center" data-testid="engineer-in-charge">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 mb-4">
              <UserCheck className="w-4 h-4" style={{ color: GOLD }} />
              <p className="label-caps" style={{ color: GOLD }}>The engineer stays in charge</p>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight leading-tight">
              What AI doesn't replace.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[#A1A1AA] leading-relaxed">
            <p>We don't leave defect detection to AI alone. AI vision makes the <span className="text-white font-medium">first cut</span> — sweeping every surface to surface cracking, spalling, corrosion and water ingress fast and consistently. Our engineers then work <span className="text-white font-medium">in conjunction</span> with it, reviewing each finding, catching what the model misses, and confirming the ones that matter.</p>
            <p>The AI never makes engineering judgements, assesses load-bearing capacity or decides remediation strategy — and it never replaces the hands-on testing that matters on site, like sounding a deck for the hollow tones that reveal delamination beneath the surface.</p>
            <p>What it does is take the laborious first pass off the engineer's plate, producing a complete, consistently documented defect inventory. Their time is redirected to the analysis and judgement that genuinely requires expertise — so inspections aren't just faster, they're more thorough.</p>
            <p className="text-white font-medium border-l-2 pl-4" style={{ borderColor: GOLD }}>AI for the first cut, a qualified engineer for the final call. Every defect that reaches your report has been seen by both.</p>
          </div>
        </div>
      </section>

      {/* Feature list + CTA */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-24 grid md:grid-cols-2 gap-10 items-center">
        <ul className="space-y-3">
          {FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-3 text-[#D4D4D8]">
              <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: GOLD }} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <div className="border border-[#27272A] bg-[#18181B] rounded-xl p-8">
          <h3 className="font-display text-2xl font-bold tracking-tight">We turn your structures into engineering reports.</h3>
          <p className="text-[#A1A1AA] mt-2">Book a Zentara inspection and receive a professional, engineer-grade report you can act on.</p>
          <a href={CONTACT_HREF} data-testid="landing-cta-footer">
            <Button className="mt-6 gap-2 h-11 px-6 font-semibold text-[#09090B] hover:-translate-y-0.5 transition-transform" style={{ background: GOLD }}>
              Request an inspection <ArrowRight className="w-4 h-4" />
            </Button>
          </a>
        </div>
      </section>

      {/* Insights teaser */}
      <section className="border-t border-[#27272A] bg-[#0C0C0E]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="label-caps mb-2" style={{ color: GOLD }}>Insights</p>
              <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight">Field notes on inspection &amp; risk.</h2>
            </div>
            <Link to="/insights" className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: GOLD }} data-testid="insights-view-all">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {INSIGHTS.slice(0, 2).map((a) => (
              <Link key={a.slug} to={`/insights/${a.slug}`} className="group block border border-[#27272A] bg-[#18181B] rounded-xl overflow-hidden hover:border-[#C5A24A]/40 transition-colors" data-testid={`landing-insight-${a.slug}`}>
                <div className="h-40 overflow-hidden">
                  <img src={a.hero} alt={a.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-[#71717A]">
                    <span style={{ color: GOLD }}>{a.tag}</span><span>·</span><span>{a.reading}</span>
                  </div>
                  <h3 className="font-display text-lg font-bold mt-2 group-hover:text-white">{a.title}</h3>
                  <p className="text-sm text-[#A1A1AA] mt-2 leading-relaxed line-clamp-2">{a.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-[#27272A] py-10 px-6 md:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src="/zentara-lockup.png" alt="ZENTARA As-Built & Engineering Group Ltd" className="w-14 h-14 object-contain rounded" />
          <div className="text-sm">
            <p className="font-display font-bold text-white">Zentara Integrity</p>
            <p className="text-[#71717A]">A product of ZENTARA As-Built &amp; Engineering Group Ltd.</p>
          </div>
        </div>
        <a href={SITE} target="_blank" rel="noreferrer" className="text-sm text-[#71717A] hover:text-white transition-colors" data-testid="footer-site-link">zentaraeng.com</a>
      </footer>
    </div>
  );
}
